import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Button, Input, Textarea, FormControl, FormLabel,
  Select, useToast, Alert, AlertIcon, Card, CardBody, CardHeader, Icon, Badge,
  Grid, GridItem, Spinner, Center, Progress, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Checkbox, InputGroup, InputLeftElement, Divider, Stat,
  StatLabel, StatNumber, StatHelpText, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure, Flex,
  Tag, TagLabel, Tooltip
} from '@chakra-ui/react';
import { FaFax, FaPlay, FaPause, FaStop, FaUpload, FaSearch, FaUsers,
  FaCheckCircle, FaTimesCircle, FaClock, FaRedo } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import faxService from '../../utils/faxService';

const BATCH_SIZE = 5; // faxes per batch
const BATCH_DELAY_MS = 10000; // 10 sec between batches

export default function FaxCampaign({ config }) {
  const [audience, setAudience] = useState('labs'); // labs, doctors, labs_directory
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState('At-Home Blood Draw Services — Special Discount for Your Patients');
  const [message, setMessage] = useState(
    'We are reaching out to offer at-home phlebotomy services for your patients. ' +
    'Our certified mobile phlebotomists come directly to your patients\' homes for convenient blood draws.\n\n' +
    'As a special introductory offer, your first patient draw is FREE.\n\n' +
    'Visit athomeblooddraw.com or call us to learn more.'
  );
  const [campaignState, setCampaignState] = useState('idle'); // idle, running, paused, completed
  const [progress, setProgress] = useState({ sent: 0, failed: 0, total: 0, current: '' });
  const [faxResults, setFaxResults] = useState([]);
  const [stateFilter, setStateFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const pauseRef = useRef(false);
  const stopRef = useRef(false);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const PAGE_SIZE = 50;

  useEffect(() => {
    fetchRecipients();
  }, [audience, page]);

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      let query;
      if (audience === 'labs') {
        query = supabase.from('labs').select('id, name, contact_phone, contact_email, fax_phone, is_active', { count: 'exact' });
        const { data, error, count } = await query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1).order('name');
        if (error) throw error;
        setRecipients((data || []).map(r => ({
          id: r.id, name: r.name, fax: r.fax_phone || r.contact_phone, email: r.contact_email, type: 'lab', active: r.is_active
        })));
        setTotalCount(count || 0);
      } else if (audience === 'doctors') {
        query = supabase.from('doctors').select('id, full_name, phone, email, clinic_name, npi_number', { count: 'exact' });
        const { data, error, count } = await query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1).order('full_name');
        if (error) throw error;
        setRecipients((data || []).map(r => ({
          id: r.id, name: r.full_name, fax: r.phone, email: r.email, org: r.clinic_name, npi: r.npi_number, type: 'doctor'
        })));
        setTotalCount(count || 0);
      } else if (audience === 'labs_directory') {
        query = supabase.from('labs_directory').select('id, company_name, phone, email, city, state', { count: 'exact' }).eq('is_active', true);
        const { data, error, count } = await query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1).order('company_name');
        if (error) throw error;
        setRecipients((data || []).map(r => ({
          id: r.id, name: r.company_name, fax: r.phone, email: r.email, location: `${r.city}, ${r.state}`, type: 'directory'
        })));
        setTotalCount(count || 0);
      }
    } catch (err) {
      toast({ title: 'Error loading recipients', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const filteredRecipients = recipients.filter(r => {
    const matchesSearch = !search || r.name?.toLowerCase().includes(search.toLowerCase()) ||
      r.fax?.includes(search) || r.email?.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  const recipientsWithFax = filteredRecipients.filter(r => r.fax && faxService.isValidFaxNumber(r.fax));

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllWithFax = () => {
    const ids = recipientsWithFax.map(r => r.id);
    setSelectedIds(new Set(ids));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleFileUpload = (e) => {
    const f = e.target.files[0];
    if (f) {
      if (f.type !== 'application/pdf') {
        toast({ title: 'Please upload a PDF file', status: 'error', duration: 3000 });
        return;
      }
      setFile(f);
    }
  };

  const startCampaign = async () => {
    if (selectedIds.size === 0) {
      toast({ title: 'No recipients selected', status: 'warning', duration: 3000 });
      return;
    }
    if (!file) {
      toast({ title: 'Please upload a PDF document to fax', status: 'warning', duration: 3000 });
      return;
    }

    onClose();
    pauseRef.current = false;
    stopRef.current = false;
    setCampaignState('running');

    const selectedRecipients = recipientsWithFax.filter(r => selectedIds.has(r.id));
    setProgress({ sent: 0, failed: 0, total: selectedRecipients.length, current: '' });
    setFaxResults([]);

    // Process in batches
    for (let i = 0; i < selectedRecipients.length; i++) {
      if (stopRef.current) break;

      while (pauseRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500));
        if (stopRef.current) break;
      }
      if (stopRef.current) break;

      const recipient = selectedRecipients[i];
      setProgress(prev => ({ ...prev, current: recipient.name }));

      try {
        const formattedFax = faxService.formatFaxNumber(recipient.fax);
        const result = await faxService.sendFax({
          toFaxNumber: formattedFax,
          file,
          fileName: file.name,
          subject,
          comments: message,
          recipientName: recipient.name,
          organization: recipient.org || recipient.name,
          coverPage: 'Standard'
        });

        await faxService.logFaxActivity({
          faxId: result.fax_id,
          toFaxNumber: formattedFax,
          fileName: file.name,
          status: 'queued'
        });

        setFaxResults(prev => [...prev, { ...recipient, status: 'sent', faxId: result.fax_id }]);
        setProgress(prev => ({ ...prev, sent: prev.sent + 1 }));
      } catch (err) {
        setFaxResults(prev => [...prev, { ...recipient, status: 'failed', error: err.message }]);
        setProgress(prev => ({ ...prev, failed: prev.failed + 1 }));
      }

      // Delay between faxes, longer delay every BATCH_SIZE
      if ((i + 1) % BATCH_SIZE === 0 && i < selectedRecipients.length - 1) {
        await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
      } else if (i < selectedRecipients.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    setCampaignState('completed');
    toast({
      title: 'Campaign finished',
      description: `Sent: ${progress.sent + 1}, Failed: ${progress.failed}`,
      status: 'success',
      duration: 5000
    });
  };

  const pauseCampaign = () => {
    pauseRef.current = true;
    setCampaignState('paused');
  };

  const resumeCampaign = () => {
    pauseRef.current = false;
    setCampaignState('running');
  };

  const stopCampaign = () => {
    stopRef.current = true;
    setCampaignState('completed');
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const progressPercent = progress.total > 0 ? ((progress.sent + progress.failed) / progress.total) * 100 : 0;

  return (
    <VStack spacing={6} align="stretch">
      {/* Campaign Setup */}
      {campaignState === 'idle' && (
        <>
          {/* Audience Selection */}
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Icon as={FaUsers} color="blue.500" />
                  <Text fontWeight="bold">Select Audience</Text>
                </HStack>
                <Badge colorScheme="blue" fontSize="sm">{totalCount} total</Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack spacing={4}>
                  <Select value={audience} onChange={(e) => { setAudience(e.target.value); setPage(0); setSelectedIds(new Set()); }}>
                    <option value="labs">Partner Labs ({audience === 'labs' ? totalCount : '...'})</option>
                    <option value="doctors">Doctors ({audience === 'doctors' ? totalCount : '...'})</option>
                    <option value="labs_directory">Labs Directory ({audience === 'labs_directory' ? totalCount : '...'})</option>
                  </Select>

                  <InputGroup maxW="300px">
                    <InputLeftElement><Icon as={FaSearch} color="gray.400" /></InputLeftElement>
                    <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
                  </InputGroup>
                </HStack>

                <HStack spacing={3}>
                  <Button size="sm" onClick={selectAllWithFax} variant="outline" colorScheme="blue">
                    Select All with Fax ({recipientsWithFax.length})
                  </Button>
                  <Button size="sm" onClick={clearSelection} variant="outline">
                    Clear Selection
                  </Button>
                  <Tag colorScheme="green" size="md">
                    <TagLabel>{selectedIds.size} selected</TagLabel>
                  </Tag>
                </HStack>

                {loading ? (
                  <Center py={8}><Spinner /></Center>
                ) : (
                  <TableContainer maxH="400px" overflowY="auto">
                    <Table size="sm">
                      <Thead position="sticky" top={0} bg="white" zIndex={1}>
                        <Tr>
                          <Th w="40px"></Th>
                          <Th>Name</Th>
                          <Th>Fax/Phone</Th>
                          <Th>Email</Th>
                          {audience === 'labs_directory' && <Th>Location</Th>}
                          <Th>Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredRecipients.map(r => {
                          const hasFax = r.fax && faxService.isValidFaxNumber(r.fax);
                          return (
                            <Tr key={r.id} opacity={hasFax ? 1 : 0.5}>
                              <Td>
                                <Checkbox
                                  isChecked={selectedIds.has(r.id)}
                                  onChange={() => toggleSelect(r.id)}
                                  isDisabled={!hasFax}
                                />
                              </Td>
                              <Td fontWeight="medium">{r.name}</Td>
                              <Td>
                                {r.fax || '—'}
                                {hasFax && <Badge ml={2} colorScheme="green" fontSize="xs">Valid</Badge>}
                                {r.fax && !hasFax && <Badge ml={2} colorScheme="red" fontSize="xs">Invalid</Badge>}
                              </Td>
                              <Td fontSize="sm" color="gray.600">{r.email || '—'}</Td>
                              {audience === 'labs_directory' && <Td fontSize="sm">{r.location || '—'}</Td>}
                              <Td>
                                {hasFax ? (
                                  <Badge colorScheme="green">Ready</Badge>
                                ) : (
                                  <Badge colorScheme="gray">No Fax</Badge>
                                )}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <HStack justify="center" spacing={2}>
                    <Button size="sm" onClick={() => setPage(Math.max(0, page - 1))} isDisabled={page === 0}>
                      Prev
                    </Button>
                    <Text fontSize="sm">Page {page + 1} of {totalPages}</Text>
                    <Button size="sm" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} isDisabled={page >= totalPages - 1}>
                      Next
                    </Button>
                  </HStack>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Campaign Content */}
          <Card>
            <CardHeader>
              <HStack spacing={3}>
                <Icon as={FaFax} color="blue.500" />
                <Text fontWeight="bold">Campaign Content</Text>
              </HStack>
            </CardHeader>
            <CardBody>
              <Grid templateColumns="1fr 1fr" gap={6}>
                <GridItem>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel>Subject Line</FormLabel>
                      <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Cover Page Message</FormLabel>
                      <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} />
                    </FormControl>
                  </VStack>
                </GridItem>

                <GridItem>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel>PDF Document (Flyer/Brochure)</FormLabel>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                      <Button
                        leftIcon={<Icon as={FaUpload} />}
                        onClick={() => fileInputRef.current?.click()}
                        w="full"
                        variant="outline"
                      >
                        {file ? file.name : 'Upload PDF'}
                      </Button>
                      {file && (
                        <Text fontSize="sm" color="green.600" mt={1}>
                          {file.name} ({(file.size / 1024).toFixed(1)} KB)
                        </Text>
                      )}
                    </FormControl>

                    <Alert status="info" borderRadius="md" mt={2}>
                      <AlertIcon />
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">Rate Limiting</Text>
                        <Text fontSize="xs">
                          Faxes are sent in batches of {BATCH_SIZE} with {BATCH_DELAY_MS / 1000}s delays between batches to avoid eFax throttling.
                        </Text>
                      </Box>
                    </Alert>
                  </VStack>
                </GridItem>
              </Grid>
            </CardBody>
          </Card>

          {/* Launch Button */}
          <Button
            leftIcon={<Icon as={FaPlay} />}
            colorScheme="blue"
            size="lg"
            onClick={onOpen}
            isDisabled={selectedIds.size === 0 || !file}
          >
            Launch Campaign ({selectedIds.size} recipients)
          </Button>
        </>
      )}

      {/* Running / Completed State */}
      {campaignState !== 'idle' && (
        <>
          {/* Progress Stats */}
          <Grid templateColumns="repeat(4, 1fr)" gap={4}>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Total</StatLabel>
                  <StatNumber>{progress.total}</StatNumber>
                  <StatHelpText>Recipients</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel color="green.500">Sent</StatLabel>
                  <StatNumber color="green.500">{progress.sent}</StatNumber>
                  <StatHelpText>
                    <Icon as={FaCheckCircle} color="green.500" mr={1} />
                    Queued
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel color="red.500">Failed</StatLabel>
                  <StatNumber color="red.500">{progress.failed}</StatNumber>
                  <StatHelpText>
                    <Icon as={FaTimesCircle} color="red.500" mr={1} />
                    Errors
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel color="orange.500">Remaining</StatLabel>
                  <StatNumber color="orange.500">{progress.total - progress.sent - progress.failed}</StatNumber>
                  <StatHelpText>
                    <Icon as={FaClock} color="orange.500" mr={1} />
                    Pending
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </Grid>

          {/* Progress Bar */}
          <Card>
            <CardBody>
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="bold">
                    {campaignState === 'running' ? 'Sending...' :
                     campaignState === 'paused' ? 'Paused' : 'Completed'}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {Math.round(progressPercent)}%
                  </Text>
                </HStack>
                <Progress
                  value={progressPercent}
                  colorScheme={campaignState === 'completed' ? 'green' : 'blue'}
                  size="lg"
                  borderRadius="md"
                  hasStripe={campaignState === 'running'}
                  isAnimated={campaignState === 'running'}
                />
                {campaignState === 'running' && progress.current && (
                  <Text fontSize="sm" color="gray.500">
                    Currently sending to: {progress.current}
                  </Text>
                )}

                <HStack spacing={3} mt={2}>
                  {campaignState === 'running' && (
                    <>
                      <Button leftIcon={<Icon as={FaPause} />} onClick={pauseCampaign} colorScheme="yellow" size="sm">
                        Pause
                      </Button>
                      <Button leftIcon={<Icon as={FaStop} />} onClick={stopCampaign} colorScheme="red" size="sm">
                        Stop
                      </Button>
                    </>
                  )}
                  {campaignState === 'paused' && (
                    <>
                      <Button leftIcon={<Icon as={FaPlay} />} onClick={resumeCampaign} colorScheme="green" size="sm">
                        Resume
                      </Button>
                      <Button leftIcon={<Icon as={FaStop} />} onClick={stopCampaign} colorScheme="red" size="sm">
                        Stop
                      </Button>
                    </>
                  )}
                  {campaignState === 'completed' && (
                    <Button leftIcon={<Icon as={FaRedo} />} onClick={() => { setCampaignState('idle'); setFaxResults([]); setProgress({ sent: 0, failed: 0, total: 0, current: '' }); }} colorScheme="blue" size="sm">
                      New Campaign
                    </Button>
                  )}
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Results Table */}
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Text fontWeight="bold">Fax Results</Text>
                <HStack spacing={2}>
                  <Button size="xs" variant={stateFilter === 'all' ? 'solid' : 'outline'} onClick={() => setStateFilter('all')}>All</Button>
                  <Button size="xs" variant={stateFilter === 'sent' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setStateFilter('sent')}>Sent</Button>
                  <Button size="xs" variant={stateFilter === 'failed' ? 'solid' : 'outline'} colorScheme="red" onClick={() => setStateFilter('failed')}>Failed</Button>
                </HStack>
              </HStack>
            </CardHeader>
            <CardBody>
              <TableContainer maxH="400px" overflowY="auto">
                <Table size="sm">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th>#</Th>
                      <Th>Recipient</Th>
                      <Th>Fax Number</Th>
                      <Th>Status</Th>
                      <Th>Details</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {faxResults
                      .filter(r => stateFilter === 'all' || r.status === stateFilter)
                      .map((r, idx) => (
                        <Tr key={idx}>
                          <Td>{idx + 1}</Td>
                          <Td fontWeight="medium">{r.name}</Td>
                          <Td>{r.fax}</Td>
                          <Td>
                            <Badge colorScheme={r.status === 'sent' ? 'green' : 'red'}>
                              {r.status === 'sent' ? 'Queued' : 'Failed'}
                            </Badge>
                          </Td>
                          <Td fontSize="xs" color="gray.500">
                            {r.faxId ? `ID: ${r.faxId}` : r.error || ''}
                          </Td>
                        </Tr>
                      ))}
                  </Tbody>
                </Table>
              </TableContainer>
              {faxResults.length === 0 && (
                <Text color="gray.500" textAlign="center" py={4}>
                  {campaignState === 'running' ? 'Results will appear as faxes are sent...' : 'No results yet'}
                </Text>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Campaign Launch</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  This will send faxes to <strong>{selectedIds.size}</strong> recipients. Each fax costs money via eFax. Please confirm.
                </Text>
              </Alert>

              <Box>
                <Text fontWeight="bold" mb={1}>Subject:</Text>
                <Text fontSize="sm" color="gray.600">{subject}</Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={1}>Document:</Text>
                <Text fontSize="sm" color="gray.600">{file?.name} ({(file?.size / 1024).toFixed(1)} KB)</Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={1}>Message Preview:</Text>
                <Box p={3} bg="gray.50" borderRadius="md" fontSize="sm" whiteSpace="pre-wrap">
                  {message}
                </Box>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={1}>Estimated Time:</Text>
                <Text fontSize="sm" color="gray.600">
                  ~{Math.ceil(selectedIds.size / BATCH_SIZE) * (BATCH_DELAY_MS / 1000 / 60) + Math.ceil(selectedIds.size * 2 / 60)} minutes
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" leftIcon={<Icon as={FaFax} />} onClick={startCampaign}>
              Send {selectedIds.size} Faxes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
